/**
 * Een gescande veldwerkopdracht (PDF zonder tekstlaag, of een foto) toch inlezen: de pagina's
 * als afbeelding naar de backend, die ze laat uitlezen (OCR door het AI-model) en tekst
 * teruggeeft. Die tekst gaat daarna door de gewone herkenning (api.extractFieldOrder).
 */
import { api } from '../../api.js';

const MAX_PAGINAS = 10;
// Lang genoeg om handschrift en kleine letters te lezen, klein genoeg om snel te versturen.
const MAX_ZIJDE = 2000;

/** Kan dit bestand een scan zijn? PDF of afbeelding. */
export function kanScanZijn(file) {
  return /\.(pdf|png|jpe?g)$/i.test(file?.name || '');
}

/** Te weinig tekst om in te lezen: dan is het (waarschijnlijk) een scan. */
export const teWeinigTekst = (text) => !text || text.trim().length < 40;

function naarJpeg(bron, breedte, hoogte) {
  const schaal = Math.min(1, MAX_ZIJDE / Math.max(breedte, hoogte));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(breedte * schaal);
  canvas.height = Math.round(hoogte * schaal);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bron, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85);
}

/** De pagina's als JPEG-data-URL's (hooguit 10). */
export async function scanPaginas(file) {
  if (/\.pdf$/i.test(file.name)) {
    const pdfjs = await import('pdfjs-dist');
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
    }
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const uit = [];
    for (let n = 1; n <= Math.min(pdf.numPages, MAX_PAGINAS); n++) {
      const pagina = await pdf.getPage(n);
      const basis = pagina.getViewport({ scale: 1 });
      const viewport = pagina.getViewport({ scale: Math.min(4, MAX_ZIJDE / Math.max(basis.width, basis.height)) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await pagina.render({ canvasContext: ctx, viewport, canvas }).promise;
      uit.push(canvas.toDataURL('image/jpeg', 0.85));
    }
    return uit;
  }
  const bmp = await createImageBitmap(file);
  return [naarJpeg(bmp, bmp.width, bmp.height)];
}

/**
 * De tekst van een gescand formulier. `status(tekst)` krijgt de stappen, voor in beeld.
 * @returns {Promise<string>}
 */
export async function tekstUitScan(projectId, file, status = () => {}) {
  status("Pagina's voorbereiden…");
  const paginas = await scanPaginas(file);
  status(`Tekst herkennen in ${paginas.length} pagina${paginas.length === 1 ? '' : "'s"}… (± 20–40 seconden)`);
  const r = await api.ocrFieldOrder(projectId, paginas, file.name);
  if (teWeinigTekst(r?.text)) throw new Error('Ook na het scannen kwam er te weinig tekst uit dit bestand.');
  // De pagina's gaan mee terug: de gebruiker ziet ze naast de herkende tekst (scan-controle.js).
  return { text: r.text, paginas };
}

/** De vraag aan de gebruiker, gedeeld door het paneel en 'Nieuw project'. */
export const SCAN_VRAAG = () => ({
  title: 'Dit lijkt een scan',
  message: 'In het bestand herkennen we geen leesbare tekst, mogelijk is het een scan. Indien gewenst kunnen we de scan omzetten naar leesbare tekst. Dit duurt 20 tot 40 seconden.',
  confirmLabel: 'Tekst herkennen',
});
